import React, { useMemo, useEffect, useState, useCallback, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import ReactFlow, {
  MiniMap,
  Controls,
  Background,
  useNodesState,
  useEdgesState,
  useReactFlow,
  MarkerType,
  useOnSelectionChange,
} from "reactflow";
import QmsAdminModal_EditWorkflowNode from "./QmsAdminModal_EditWorkflowNode";
import "reactflow/dist/style.css";
import "./qmsAdminWorkflow.css";
import { QMS } from "../../../_constants/actionConstants";
import _ from "lodash";

export default function QmsAdminWorkflowEditor(props) {
  const { divisionId, workflow, onNodeSelect } = props;

  const dispatch = useDispatch();
  const { project } = useReactFlow();
  const reactFlowWrapper = useRef(null);
  const connectingNodeId = useRef(null);
  const selectedNode = useRef(null);
  const nodeExists = useRef(false); // Used to carry value between onConnect() and onConnectEnd()
  const newNodePosition = useRef(null); // Used to carry value between onConnectEnd() and addNode()

  const [nodes, setNodes, onNodesChange] = useNodesState(workflow.nodes || []);
  const [edges, setEdges, onEdgesChange] = useEdgesState(workflow.edges || []);

  const [modalState_editNode, setModalState_editNode] = useState({
    showModal: false,
    data: null,
    submitCallback: null,
  });
  const toggleModal_editNode = () =>
    setModalState_editNode({
      ...modalState_editNode,
      showModal: !modalState_editNode.showModal,
    });

  useEffect(
    () =>
      dispatch({
        type: QMS.WORKFLOW.EDIT,
        workflowId: workflow.workflowId,
        fieldsToEdit: { nodes },
      }),
    [nodes]
  );
  useEffect(
    () =>
      dispatch({
        type: QMS.WORKFLOW.EDIT,
        workflowId: workflow.workflowId,
        fieldsToEdit: { edges },
      }),
    [edges]
  );

  useEffect(() => selectedNode.current && onNodeSelect(), [selectedNode.current]);

  const getNextNodeId = () => {
    const existNodeIds = (nodes || []).map((n) => +n.id);
    const nextNodeId = Math.max(...existNodeIds, 0) + 1;
    return `${nextNodeId}`;
  };

  const addNode = useCallback(
    (newNodeData) => {
      const id = getNextNodeId();
      const newNode = {
        id,
        type: (nodes || []).length === 0 ? "input" : null,
        position: newNodePosition.current ? newNodePosition.current : { x: 0, y: 0 },
        data: newNodeData,
      };

      setNodes((nds) => nds.concat(newNode));

      // Add edge only if a node is being added by an 'edge drag'.
      if (connectingNodeId.current)
        setEdges((eds) =>
          eds.concat({
            id: `e${connectingNodeId.current}-${id}`,
            source: connectingNodeId.current,
            target: id,
            markerEnd: {
              type: MarkerType.ArrowClosed,
              width: 15,
              height: 15,
            },
          })
        );
    },
    [nodes]
  );

  const updateNode = useCallback(
    (updatedNodeData) => {
      setNodes((nds) =>
        nds.map((node) => {
          if (node.id === updatedNodeData.id) {
            node.data = {
              label: updatedNodeData.label,
              title: updatedNodeData.title,
              description: updatedNodeData.description,
              ownerTeamName: updatedNodeData.ownerTeamName,
              ownerTeamId: updatedNodeData.ownerTeamId,
              duration: updatedNodeData.duration,
              relationshipTypeValue: updatedNodeData.relationshipTypeValue,
            };
          }
          return node;
        })
      );
    },
    [nodes, setNodes]
  );

  const onConnect = useCallback(
    (params) => {
      const edgeExists = (edges || []).some(
        (e) => e.source === params.source && e.target === params.target
      );
      nodeExists.current = (nodes || []).some((n) => n.id === params.target);

      // Add edge only if the edge is not a duplicate
      if (!edgeExists)
        setEdges((eds) =>
          eds.concat({
            id: `e${params.source}-${params.target}`,
            source: params.source,
            target: params.target,
            markerEnd: {
              type: MarkerType.ArrowClosed,
              width: 15,
              height: 15,
            },
          })
        );
    },
    [nodes, edges]
  );

  // Drag started from a handle
  const onConnectStart = useCallback((_, { nodeId }) => {
    connectingNodeId.current = nodeId; // This becomes the source id once new edge is created

    // Reset nodeExists
    nodeExists.current = false;
  }, []);

  // Called after onConnect()
  const onConnectEnd = useCallback(
    (event) => {
      const targetIsPane = event.target.classList.contains("react-flow__pane");

      // Show modal to add new node only if the target is not an existing node
      // (and if the target is within bounds).
      if (targetIsPane && !nodeExists.current) {
        // we need to remove the wrapper bounds, in order to get the correct position
        const { top, left } = reactFlowWrapper.current.getBoundingClientRect();
        newNodePosition.current = project({
          x: event.clientX - left - 75,
          y: event.clientY - top,
        });
        showModal_editNode();
        nodeExists.current = false;
      }
    },
    [project, nodes, edges]
  );

  const onNodeClick = useCallback((event, node) => {
    connectingNodeId.current = node.id;
    selectedNode.current = node;
  }, []);

  const onNodeDoubleClick = useCallback((event, node) => {
    connectingNodeId.current = node.id;
    selectedNode.current = node;

    setModalState_editNode({
      ...modalState_editNode,
      showModal: true,
      data: { ...node.data, id: node.id },
      submitCallback: updateNode,
    });
  }, []);

  const showModal_editNode = () => {
    setModalState_editNode({
      ...modalState_editNode,
      showModal: true,
      submitCallback: addNode,
    });
  };

  return (
    <>
      <div className="card">
        <div className="card-header p-2 d-flex flex-row">
          <div>
            <button
              id="addNode"
              name="addNode"
              className="btn btn-sm btn-secondary"
              onClick={() => {
                showModal_editNode();
                connectingNodeId.current = null;
              }}
            >
              Add Node...
            </button>
          </div>
        </div>
        <div
          className="card-body p-2 wrapper"
          ref={reactFlowWrapper}
          style={{ height: "500px", width: "100%" }}
        >
          <ReactFlow
            nodes={nodes}
            edges={edges}
            onNodesChange={onNodesChange}
            onEdgesChange={onEdgesChange}
            onConnect={onConnect}
            onConnectStart={onConnectStart}
            onConnectEnd={onConnectEnd}
            onNodeClick={onNodeClick}
            onNodeDoubleClick={onNodeDoubleClick}
            fitView
            fitViewOptions={{
              padding: 3,
            }}
          >
            <MiniMap />
            <Controls />
            <Background />
          </ReactFlow>
        </div>
      </div>
      {modalState_editNode.showModal && (
        <QmsAdminModal_EditWorkflowNode
          divisionId={divisionId}
          workflowId={workflow.workflowId}
          modalState={modalState_editNode}
          toggleModal={toggleModal_editNode}
        />
      )}
    </>
  );
}
